'use client'

import { useId, useRef, useState } from 'react'
import type { ComponentProps } from 'react'
import { clsx } from 'clsx'
import { DayButton, DayPicker } from 'react-day-picker'
import type { ChevronProps, DayButtonProps } from 'react-day-picker'
import { enGB } from 'react-day-picker/locale'
import { t } from '@/lang'
import { isoFromUk } from '@/lib/date'
import { isInvalid } from '@/ui/Field'
import { CalendarIcon } from '@/ui/icons/CalendarIcon'
import { ChevronIcon } from '@/ui/icons/ChevronIcon'
import { Input } from '@/ui/Input'

type Props = Omit<ComponentProps<'input'>, 'type' | 'value' | 'onChange' | 'onBlur' | 'min' | 'max'> & {
  id: string
  /** What the field holds, meant as `dd/mm/yyyy`; part of a date while it is being typed. */
  value: string
  onChange: (value: string) => void
  /** Fires once focus leaves both the field and its calendar, not when the calendar takes it. */
  onBlur?: () => void
  /** First and last day that can be picked, ISO `yyyy-mm-dd` like a native date input's. */
  min: string
  max: string
}

// A bare `yyyy-mm-dd` parses as UTC midnight; DayPicker counts days in the browser's own zone.
const dayOf = (iso: string) => new Date(`${iso}T00:00`)

/** Digits only, with the slashes put in as the day and month fill up: `16102026` reads `16/10/2026`. */
const asTyped = (text: string) => {
  const digits = text.replace(/\D/g, '').slice(0, 8)
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('/')
}

/**
 * Date field in the design's `dd/mm/yyyy` with the calendar under it (APG date picker combobox).
 * A click anywhere on the field opens the calendar and leaves the caret in the field; ArrowDown
 * moves focus into the days, where arrows move by day, PageUp/PageDown by month, Escape closes.
 */
export function DatePicker({ id, value, onChange, onBlur, min, max, ...props }: Props) {
  const picked = isoFromUk(value)
  const [open, setOpen] = useState(false)
  const [grabFocus, setGrabFocus] = useState(false)
  const [month, setMonth] = useState(() => dayOf(picked ?? min))
  const panelId = useId()
  const wrapper = useRef<HTMLSpanElement>(null)
  const invalid = isInvalid(props['aria-invalid'])

  const openCalendar = (withFocus: boolean) => {
    if (!open) setMonth(dayOf(picked ?? min))
    setGrabFocus(withFocus)
    setOpen(true)
  }

  const close = () => {
    setOpen(false)
    wrapper.current?.querySelector('input')?.focus()
  }

  return (
    <span
      ref={wrapper}
      className="relative block"
      onBlur={(event) => {
        // Moving between the field and the calendar is not leaving the control.
        if (event.currentTarget.contains(event.relatedTarget)) return
        setOpen(false)
        onBlur?.()
      }}
    >
      <Input
        id={id}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- An input that opens a calendar is the APG date picker combobox; no tag covers it.
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        autoComplete="off"
        inputMode="numeric"
        maxLength={10}
        placeholder={t('date-picker.placeholder')}
        value={value}
        filter={asTyped}
        // Room for the calendar glyph; when invalid, Input makes the same room for its error icon.
        className={clsx('cursor-pointer', !invalid && 'pe-9')}
        onChange={(event) => {
          const next = event.currentTarget.value
          onChange(next)
          const iso = isoFromUk(next)
          if (iso) setMonth(dayOf(iso))
        }}
        onClick={() => openCalendar(false)}
        onKeyDown={(event) => {
          // Space types nothing in a digits-only field, so it is free to open the calendar as well.
          if (event.key === 'ArrowDown' || event.key === ' ') {
            event.preventDefault()
            if (!open) return openCalendar(true)
            wrapper.current?.querySelector<HTMLButtonElement>('dialog td button[tabindex="0"]')?.focus()
          } else if (event.key === 'Escape' && open) {
            event.preventDefault()
            setOpen(false)
          }
        }}
        {...props}
      />
      {/* Outside Input's `trailing` slot: that slot takes clicks, and a click here must reach the field. */}
      {invalid ? null : (
        <CalendarIcon className="pointer-events-none absolute inset-e-2.5 top-1/2 w-5 -translate-y-1/2 text-primary-500" />
      )}
      {open ? (
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Escape closes the calendar from whichever control inside holds focus.
        <dialog
          open
          id={panelId}
          aria-label={t('date-picker.dialog-label')}
          // Focusable, so a click on the panel's padding keeps focus inside and does not close it.
          tabIndex={-1}
          className="absolute start-0 inset-e-auto top-full z-10 m-0 mt-1 w-72.5 rounded-[0.9375rem] border border-neutral-400 bg-white px-1.25 pt-2.5 pb-1.25 focus:outline-none"
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return
            event.preventDefault()
            close()
          }}
        >
          <DayPicker
            mode="single"
            required
            locale={enGB}
            autoFocus={grabFocus}
            showOutsideDays
            navLayout="around"
            selected={picked ? dayOf(picked) : undefined}
            month={month}
            onMonthChange={setMonth}
            startMonth={dayOf(min)}
            endMonth={dayOf(max)}
            disabled={[{ before: dayOf(min) }, { after: dayOf(max) }]}
            onSelect={(date) => {
              onChange(date.toLocaleDateString('en-GB'))
              close()
            }}
            components={{ Chevron, DayButton: Day }}
            classNames={{
              root: 'text-neutral-800',
              month: 'grid grid-cols-[auto_1fr_auto] items-center gap-y-px',
              month_caption: 'pt-1.25 pb-2.5 text-center text-section',
              button_previous: navButton,
              button_next: navButton,
              month_grid: 'col-span-3 border-collapse',
              weekday: 'size-10 p-0 text-calendar-weekday text-neutral-400',
              day: 'size-10 p-0 text-center',
            }}
          />
        </dialog>
      ) : null}
    </span>
  )
}

const navButton = clsx(
  'mx-1 mb-1.25 flex size-8 cursor-pointer items-center justify-center rounded-sm text-primary-500',
  'focus-visible:focus-ring aria-disabled:cursor-not-allowed aria-disabled:text-neutral-300'
)

function Chevron({ orientation }: ChevronProps) {
  return <ChevronIcon className={clsx('w-2', orientation === 'right' && '-scale-x-100')} />
}

/** Penpot `UI elements / calendar date`: one state per day, so the classes never compete. */
function Day(props: DayButtonProps) {
  const { selected, disabled, outside, today } = props.modifiers
  return (
    <DayButton
      {...props}
      className={clsx(
        'size-10 rounded-full text-calendar-day focus-visible:focus-ring',
        selected
          ? 'bg-accent-500 text-white'
          : disabled
            ? 'cursor-not-allowed text-neutral-300'
            : clsx('cursor-pointer hover:bg-neutral-200', today && 'bg-neutral-200', outside && 'text-neutral-300')
      )}
    />
  )
}
